import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Sparkles, Check } from "lucide-react";
import { Logo } from "./Logo";
import { CtaButton } from "./CtaButton";

const LINES = [
  { t: "Receba mais de" },
  { t: "25 mil materiais", c: "text-plum" },
  { t: "pedagógicos prontos" },
  { t: "para revender!", hl: true },
];
const ease = [0.16, 1, 0.3, 1];

const MaskedLine = ({ line, i }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${line.c || ""}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.12, ease }}
    >
      {line.hl ? <span className="hl-mark sm:whitespace-nowrap">{line.t}</span> : line.t}
    </motion.span>
  </span>
);

const HeroImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -60]);
  const rx = useSpring(useMotionValue(0), { stiffness: 80, damping: 15 });
  const ry = useSpring(useMotionValue(0), { stiffness: 80, damping: 15 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 8);
  };
  return (
    <div ref={ref} className="relative mx-auto mt-10 w-full max-w-5xl [perspective:1400px]" onMouseMove={onMove} onMouseLeave={() => { rx.set(0); ry.set(0); }}>
      <div className="spotlight absolute inset-x-[8%] top-[10%] bottom-0 -z-0 rounded-full" />
      <motion.img
        src="/img/hero-renda-kits-v3.webp"
        alt="Celulares com catálogo de kits de atividades prontos para vender, ao lado de kits impressos"
        width={1536}
        height={964}
        fetchPriority="high"
        data-testid="hero-image"
        style={{ y, rotateX: rx, rotateY: ry }}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.5, ease }}
        className="relative z-10 h-auto w-full drop-shadow-[0_40px_50px_rgba(26,16,48,0.28)]"
      />
    </div>
  );
};

export const Hero = () => (
  <header className="hero-bg relative overflow-hidden pb-16 pt-6 sm:pb-24" data-testid="hero-section">
    <div className="grain" />
    <div className="relative mx-auto max-w-6xl px-5">
      <nav className="flex items-center justify-between">
        <Logo />
        <span className="hidden rounded-full border border-ink/10 bg-white/70 px-4 py-2 text-xs font-bold uppercase tracking-widest text-ink/70 backdrop-blur sm:inline-flex">
          Licença PLR inclusa
        </span>
      </nav>
      <div className="mt-12 grid gap-8 lg:mt-16 lg:grid-cols-12 lg:items-end">
        <h1 className="font-display text-[2.6rem] font-extrabold uppercase leading-[0.95] tracking-tight text-ink sm:text-6xl lg:col-span-8 lg:text-[4.9rem]">
          {LINES.map((l, i) => <MaskedLine key={l.t} line={l} i={i} />)}
        </h1>
        <motion.div
          className="lg:col-span-4 lg:pb-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8, ease }}
        >
          <p className="flex items-start gap-2 text-base font-semibold text-ink/80 sm:text-lg">
            <Sparkles className="mt-1 h-5 w-5 shrink-0 text-rose" />
            Venda kits digitais pelo celular, sem precisar aparecer!
          </p>
          <CtaButton target="contas" testId="hero-cta-button" className="mt-6 w-full sm:w-auto">
            Quero começar minha renda extra
          </CtaButton>
          <ul className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-sm font-semibold text-ink/70">
            {["Pagamento único", "Acesso imediato", "Garantia de 7 dias"].map((t) => (
              <li key={t} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-mint" />{t}</li>
            ))}
          </ul>
        </motion.div>
      </div>
      <HeroImage />
    </div>
  </header>
);
